<script setup lang="ts">
import { userUserStore } from "../../../stores/user";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  UserCredential,
  signInWithRedirect,
  getRedirectResult,
  User,
  signOut,
  signInWithCustomToken
} from "firebase/auth";
import Lucide from "../../../base-components/Lucide";
import Button from "../../../base-components/Button";
import { FormInput, FormLabel, FormHelp } from "../../../base-components/Form";
import Alert from "../../../base-components/Alert";
import { computed, onBeforeMount, onMounted, ref } from "vue";
import LoadingIcon from "../../../base-components/LoadingIcon";
import MobileDetect from "mobile-detect";
import { loginMagicLinkEnviarMail } from "../../../services/cloudFunctions/loginMagicLinkEnviarMail";
import { loginCodigoMailEnviar } from "../../../services/cloudFunctions/loginCodigoMailEnviar";
import { loginMagicLinkAcceder } from "../../../services/cloudFunctions/loginMagicLinkAcceder";
import { loginCodigoMailAcceder } from "../../../services/cloudFunctions/loginCodigoMailAcceder";

const userStore = userUserStore();
const cargando = ref(false);
const cargandoMensaje = ref("Cargando...");
const mecanismo = ref("servicios");

const inicioSesionMail = ref("");
const inicioSesionContrasena = ref("");
const inicioSesionMensajeError = ref("");
const inicioSesionMensajeExito = ref("");

const inicioSesionRUT = ref("");
const inicioSesionUsuario = ref("");
const inicioSesionRUTUsuarioClave = ref("");
const inicioSesionRUTMensajeError = ref("");
const usuarioInvalido = computed(() => {
  if (inicioSesionUsuario.value.includes(" ")) {
    return "Usuario inválido, hay un espacio en blanco.";
  }
  if (inicioSesionUsuario.value.includes("@")) {
    return "Usuario inválido. Los usuarios no tienen @";
  }
  return "";
});

const useMagicLinkRealizandoLogin = ref(false);
const useMagicLinkAccesoMensajeError = ref("");
const useCodigoRealizandoLogin = ref(false);
const useCodigoAccesoMensajeError = ref("");
const useCodigoAccesoMensajeExito = ref("");
const useCodigoAccesoCargando = ref(false);
const codigoEnviado = ref(false);
const enviarMailCodigoExito = ref("");
const enviarMailCodigoError = ref("");
const useMagicLinkCargando = ref(false);
const useCodigoCargando = ref(false);
const useMagicLinkRequiereTelefono = ref(false);
const useMagicLinkEmail = ref("");
const useMagicLinkMensajeError = ref("");
const useMagicLinkMensajeExito = ref("");
const tokenDispositivo = ref("");
const ambiente = import.meta.env.VITE_ENV_NOMBRE;
const esMobile = ref(false);
const verPassword = ref(false);

const emit = defineEmits<{
  (e: "loginexitoso"): void;
}>();

const procesarLoginMagicLink = () => {
  mecanismo.value = "magicLink";
  const locationhref = window.location.href;
  const url = new URL(locationhref);
  const tokenMagicLink = url.searchParams.get("tokenMagicLink");
  if (tokenMagicLink != null) {
    useMagicLinkRealizandoLogin.value = true;
    useMagicLinkCargando.value = true;
    window.localStorage.setItem("timeLogin", "");

    loginMagicLinkAcceder({
      tokenAcceso: tokenMagicLink,
      telefono: ""
    }).then(response => {
      useMagicLinkAccesoMensajeError.value = "";
      useMagicLinkMensajeError.value = "";
      useMagicLinkMensajeExito.value = "";
      if (response.exito) {
        // Tengo el token de acceso
        const auth = getAuth();
        signInWithCustomToken(auth, response.tokenAcceso)
          .then(result => {
            if (result != null) {
              procesarAcceso(result);
            }
          })
          .catch(error => {
            const errorCode = error.code;
            console.error("errorCode: ", errorCode);
            const errorMessage = error.message;
            useMagicLinkAccesoMensajeError.value = errorMessage;
            useMagicLinkCargando.value = false;
          });
      } else if (response.exito == false && response.requiereTelefono == true) {
        useMagicLinkRequiereTelefono.value = true;
        useMagicLinkAccesoMensajeError.value = response.errorMsg;
        useMagicLinkMensajeError.value = response.errorMsg;
        useMagicLinkCargando.value = false;
        useMagicLinkRealizandoLogin.value = false;
      } else {
        useMagicLinkAccesoMensajeError.value = response.errorMsg;
        useMagicLinkMensajeError.value = response.errorMsg;
        useMagicLinkCargando.value = false;
        useMagicLinkRealizandoLogin.value = false;
        useMagicLinkRequiereTelefono.value = false;
      }
    });
  }
};

const procesarLoginCodigo = (tokenNumerico: string) => {
  useCodigoRealizandoLogin.value = false;

  mecanismo.value = "codigo";

  if (tokenNumerico && tokenDispositivo.value) {
    useCodigoRealizandoLogin.value = true;
    useCodigoAccesoCargando.value = true;
    window.localStorage.setItem("timeLogin", "");

    loginCodigoMailAcceder({
      mail: useMagicLinkEmail.value,
      tokenNumerico: tokenNumerico,
      tokenDispositivo: tokenDispositivo.value
    }).then(response => {
      useCodigoAccesoMensajeError.value = "";
      useCodigoAccesoMensajeExito.value = "";
      if (response.exito) {
        // Tengo el token de acceso
        const auth = getAuth();
        signInWithCustomToken(auth, response.adicionalData)
          .then(result => {
            if (result != null) {
              procesarAcceso(result);
            }
          })
          .catch(error => {
            const errorCode = error.code;
            console.error("errorCode: ", errorCode);
            const errorMessage = error.message;
            useCodigoAccesoMensajeError.value = errorMessage;
            useCodigoAccesoCargando.value = false;
            code.value = code.value.map(() => "");
            inputs.value[0]?.focus();
          });
      } else {
        useCodigoAccesoMensajeError.value = response.errorMsg;
        useCodigoAccesoCargando.value = false;
        code.value = code.value.map(() => "");
        inputs.value[0]?.focus();
      }
    });
  }
};

const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

onBeforeMount(() => {
  esMobile.value = isMobile();
  // Veo si ya inicio sesion
  const auth = getAuth();
  if (auth.currentUser != null) {
    procesarAccesoUser(auth.currentUser);
  } else {
    const locationhref = window.location.href;
    // if (locationhref.includes("tokenMagicLink")) {
    //   procesarLoginMagicLink();
    // } else {
    // No es magiclink
    const timeLoginStr = window.localStorage.getItem("timeLogin");
    if (timeLoginStr == "" || timeLoginStr == null) {
      return;
    }
    const timeLogin = parseInt(timeLoginStr);
    if (isNaN(timeLogin)) {
      return;
    }

    const now = new Date().getTime();
    if (Math.abs(now - timeLogin) < 1000 * 60 * 4) {
      // Intento loguearse hace menos de 5 minutos
      cargando.value = true;
      cargandoMensaje.value = "Validando usuario...";
      const auth = getAuth();
      getRedirectResult(auth)
        .then(async result => {
          if (result != null) {
            procesarAcceso(result);
          }
          window.localStorage.setItem("timeLogin", "");
          cargando.value = false;
        })
        .catch(error => {
          cargando.value = false;
          const email = error.customData.email;
          window.localStorage.setItem("timeLogin", "");
          console.error(`Error en el login de ${email}:`, error);
          // Handle Errors here.
          // The email of the user's account used.
          // The AuthCredential type that was used.
          const credential = GoogleAuthProvider.credentialFromError(error);
          console.error("Error en el login-credential:", credential);
          // ...
        });
    }
  }
  // }
});

const procesarAcceso = (userCredential: UserCredential) => {
  // Load user data
  const user = userCredential.user;
  userStore.setUser(user);
  emit("loginexitoso");
};

const procesarAccesoUser = (user: User) => {
  // Load user data
  userStore.setUser(user);
  emit("loginexitoso");
};

const cargandoLoginConMailClave = ref(false);
const handlerIniciarSesionUsuarioContrasena = async () => {
  cargandoLoginConMailClave.value = true;
  inicioSesionMensajeError.value = "";
  try {
    // Crea el usuario con correo electrónico y contraseña
    const auth = getAuth();
    try {
      cargandoMensaje.value = "Validando usuario...";
      const userCredential = await signInWithEmailAndPassword(
        auth,
        inicioSesionMail.value,
        inicioSesionContrasena.value
      );
      procesarAcceso(userCredential);
      userStore.setMecanismoInicioSesion("password");
      cargandoLoginConMailClave.value = false;
    } catch (error) {
      cargandoLoginConMailClave.value = false;
      inicioSesionMensajeError.value =
        "Usuario y/o contraseña incorrectos. Intenta iniciar sesión utilizando el link por mail. Por ayuda, contactate con soporte@fixed.uy";
      console.error("Error en el registro:", error);
    }
  } catch (error) {
    console.error("Error en el registro:", error);
  }
};

const cargandoLoginRUT = ref(false);
const handlerIniciarSesionRUT = async () => {
  cargandoLoginRUT.value = true;
  inicioSesionRUTMensajeError.value = "";
  try {
    // Crea el usuario con correo electrónico y contraseña
    const auth = getAuth();
    const mail = `${inicioSesionUsuario.value}@${inicioSesionRUT.value}.fixed.uy`;
    try {
      cargandoMensaje.value = "Validando usuario...";
      const userCredential = await signInWithEmailAndPassword(
        auth,
        mail,
        inicioSesionRUTUsuarioClave.value
      );
      procesarAcceso(userCredential);
      userStore.setMecanismoInicioSesion("password");
      cargandoLoginRUT.value = false;
    } catch (error) {
      cargandoLoginRUT.value = false;
      inicioSesionRUTMensajeError.value =
        "Usuario y/o contraseña incorrectos. Por ayuda, contactate con soporte@fixed.uy";
      console.error("Error en el registro:", error);
    }
  } catch (error) {
    console.error("Error en el registro:", error);
  }
};

const googleSignIn = () => {
  const provider = new GoogleAuthProvider();
  const auth = getAuth();
  window.localStorage.setItem("timeLogin", new Date().getTime().toString());
  // signInWithRedirect(auth, provider);
  userStore.setMecanismoInicioSesion("google");
  if (ambiente == "Desarrollo") {
    signInWithPopup(auth, provider)
      .then(async result => {
        await procesarAcceso(result);
      })
      .catch(error => {
        // Handle Errors here.
        console.error("** error ingresando");
        const errorCode = error.code;
        const errorMessage = error.message;
        // The email of the user's account used.
        // The AuthCredential type that was used.
        const credential = GoogleAuthProvider.credentialFromError(error);
        console.error(errorCode);
        console.error(errorMessage);
        console.error(credential);
      });
  } else {
    signInWithRedirect(auth, provider);
  }
};

const microsoftSignIn = () => {
  const provider = new OAuthProvider("microsoft.com");
  const auth = getAuth();
  userStore.setMecanismoInicioSesion("microsoft");
  window.localStorage.setItem("timeLogin", new Date().getTime().toString());
  signInWithRedirect(auth, provider);
  // signInWithPopup(auth, provider)
  //   .then(async result => {
  //     await procesarAcceso(result);
  //   })
  //   .catch(error => {
  //     // Handle Errors here.
  //     console.error("** error ingresando");
  //     const errorCode = error.code;
  //     const errorMessage = error.message;
  //     // The email of the user's account used.
  //     // The AuthCredential type that was used.
  //     const credential = GoogleAuthProvider.credentialFromError(error);
  //     console.error(errorCode);
  //     console.error(errorMessage);
  //     console.error(credential);
  //   });
};

const handlerEnviarMail = async () => {
  useMagicLinkMensajeError.value = "";
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailRegex.test(useMagicLinkEmail.value) == false) {
    useMagicLinkMensajeError.value = "El mail ingresado no es válido.";
    return;
  }
  userStore.setMecanismoInicioSesion("magicLink");
  useMagicLinkCargando.value = true;
  const response = await loginMagicLinkEnviarMail({
    mail: useMagicLinkEmail.value,
    urlBase: window.location.origin,
    destino: "login"
  });
  useMagicLinkMensajeExito.value = "";
  useMagicLinkMensajeError.value = "";

  if (response.exito) {
    useMagicLinkMensajeExito.value = `Te hemos enviado un mail con el link a ${useMagicLinkEmail.value} para iniciar sesión. Por las dudas revisa la carpeta de Spam o correo no deseado.`;
  } else {
    const errorMessage = response.errorMsg;
    useMagicLinkMensajeExito.value = "";
    useMagicLinkMensajeError.value = errorMessage;
  }
  useMagicLinkCargando.value = false;
};

const handlerEnviarMailCodigo = async () => {
  mecanismo.value = "codigo";
  useCodigoRealizandoLogin.value = false;
  enviarMailCodigoError.value = "";
  enviarMailCodigoExito.value = "";
  useCodigoCargando.value = true;
  codigoEnviado.value = false;
  useCodigoAccesoMensajeError.value = "";
  useCodigoAccesoMensajeExito.value = "";
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailRegex.test(useMagicLinkEmail.value) == false) {
    enviarMailCodigoError.value = "El mail ingresado no es válido.";
    return;
  }
  const md = new MobileDetect(window.navigator.userAgent);
  const dispositivo = md.tablet()
    ? "Tablet"
    : md.mobile()
      ? "Celular"
      : "Computadora";

  userStore.setMecanismoInicioSesion("codigo");

  const response = await loginCodigoMailEnviar({
    mail: useMagicLinkEmail.value,
    dispositivo: dispositivo
  });

  if (response?.exito) codigoEnviado.value = true;

  if (response?.adicionalData) tokenDispositivo.value = response.adicionalData;

  if (response.exito) {
    enviarMailCodigoExito.value = `Hemos enviado el código de inicio de sesión a ${useMagicLinkEmail.value} desde acceso@fixed.uy`;
  } else {
    const errorMessage = response.errorMsg;
    enviarMailCodigoExito.value = "";
    enviarMailCodigoError.value = errorMessage;
  }
  useCodigoCargando.value = false;
};

const reintentarMagicLicnk = () => {
  useMagicLinkAccesoMensajeError.value = "";
  useMagicLinkRealizandoLogin.value = false;
  mecanismo.value = "servicios";
};

const handlerPassword = () => {
  verPassword.value = !verPassword.value;
};

const cancelarLogin = () => {
  userStore.signOut();
  signOut(getAuth());
};

const codeLength = 6;
const code = ref<string[]>(Array(codeLength).fill(""));

const inputs = ref<(HTMLInputElement | null)[]>([]);

onMounted(() => {
  inputs.value[0]?.focus();
});

const handleInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;

  if (/^\d$/.test(value)) {
    code.value[index] = value;

    if (index < codeLength - 1) {
      inputs.value[index + 1]?.focus();
    }
  } else {
    code.value[index] = "";
  }

  const completo = code.value.every(digit => digit !== "");

  if (completo) {
    procesarLoginCodigo(code.value.join(""));
  }
};

const handleBackspace = (index: number, event: KeyboardEvent) => {
  if (code.value[index] === "") {
    if (index > 0) {
      inputs.value[index - 1]?.focus();
    }
  }
};
</script>

<template>
  <div v-if="!cargando">
    <div v-if="mecanismo == 'servicios'" class="intro-x flex flex-col">
      <h2
        class="text-2xl font-bold text-center intro-x xl:text-3xl xl:text-left mb-5"
      >
        Accede con:
      </h2>
      <div
        id="customBtn"
        class="customGPlusSignIn mx-auto xl:mx-0"
        @click="googleSignIn"
      >
        <span class="iconProvider"><img src="/sign_in_google.svg" /></span>
        <span class="buttonText">Google - Gmail</span>
      </div>
      <div
        id="customBtn"
        class="customMicrosoftSignIn mt-5 mx-auto xl:mx-0"
        @click="microsoftSignIn"
      >
        <span class="iconProvider"><img src="/sign_in_microsoft.svg" /></span>
        <span class="buttonText">Microsoft - Hotmail</span>
      </div>
      <div
        id="customBtn"
        class="mt-5 mx-auto xl:mx-0"
        @click="
          () => (
            (mecanismo = 'magicLink'),
            (useMagicLinkEmail = ''),
            (useMagicLinkMensajeError = '')
          )
        "
      >
        <div class="flex p-2">
          <div><Lucide icon="Mail" /></div>
          <div class="ml-5 text-center font-bold flex-1">Mi mail</div>
        </div>
      </div>
      <div
        id="customBtn"
        class="mt-5 mx-auto xl:mx-0"
        @click="() => (mecanismo = 'usuarioContrasena')"
      >
        <div class="flex p-2">
          <div><Lucide icon="Lock" /></div>
          <div class="ml-5 text-center font-bold flex-1">Mail y clave</div>
        </div>
      </div>
      <div
        id="customBtn"
        class="mt-5 mx-auto xl:mx-0"
        @click="() => (mecanismo = 'usuarioRUTContrasena')"
      >
        <div class="flex p-2">
          <div><Lucide icon="Triangle" /></div>
          <div class="ml-5 text-center font-bold flex-1">
            RUT, usuario y clave
          </div>
        </div>
      </div>
    </div>
    <div
      v-else-if="mecanismo == 'usuarioContrasena'"
      class="mt-8 intro-x flex flex-col"
    >
      <div class="mt-3">
        <h3 class="text-xl font-bold text-center intro-x xl:text-left mb-5">
          Iniciar sesión
        </h3>
        <div v-if="esMobile" class="flex-1 text-right">
          <Button
            variant="primary"
            class="mb-2 mr-1"
            @click="handlerIniciarSesionUsuarioContrasena"
          >
            Acceder
          </Button>
        </div>
        <div class="flex flex-col">
          <div class="mb-4 text-left">
            <FormLabel htmlFor="ingresa-mail-registro"
              >Ingresa tu mail</FormLabel
            >
            <FormInput
              id="ingresa-mail-registro"
              v-model="inicioSesionMail"
              type="text"
              placeholder="Ingresa tu mail"
            />
          </div>
          <div class="flex">
            <div class="mb-4 text-left">
              <FormLabel htmlFor="ingresa-contrasena-registro"
                >Ingresa tu contraseña para Atiendo</FormLabel
              >
              <FormInput
                id="ingresa-contrasena-registro"
                v-model="inicioSesionContrasena"
                :type="verPassword ? 'text' : 'password'"
                placeholder="Ingresa tu contraseña"
              />
            </div>
            <Lucide
              @click="handlerPassword"
              icon="Eye"
              class="w-4 h-4 m-3 cursor-pointer mt-10"
            />
          </div>
        </div>
      </div>
      <div v-if="inicioSesionMensajeError != ''" class="mt-3 text-left">
        <div class="text-danger">
          {{ inicioSesionMensajeError }}
        </div>
      </div>
      <div v-if="inicioSesionMensajeExito != ''" class="mt-3 text-left">
        <Alert variant="outline-success" class="flex items-center mb-2">
          <Lucide icon="Lock" class="w-6 h-6 mr-2" />
          {{ inicioSesionMensajeExito }}
        </Alert>
      </div>
      <div v-if="!cargandoLoginConMailClave" class="flex my-6">
        <!-------------------- Botón Cancelar ------------------------>
        <div class="flex-1 text-left">
          <Button
            variant="secondary"
            class="mb-2 mr-1"
            @click="() => (mecanismo = 'servicios')"
          >
            Cancelar
          </Button>
        </div>
        <!-------------------- Botón Crear nuevo ------------------------>
        <div v-if="!esMobile" class="flex-1 text-right">
          <Button
            variant="primary"
            class="mb-2 mr-1"
            @click="handlerIniciarSesionUsuarioContrasena"
          >
            Acceder
          </Button>
        </div>
      </div>

      <div v-else class="flex justify-center my-6">
        <div class="flex">
          <LoadingIcon icon="oval" class="w-8 h-8" />
        </div>
      </div>
    </div>
    <div
      v-else-if="mecanismo == 'usuarioRUTContrasena'"
      class="mt-8 intro-x flex flex-col"
    >
      <div class="mt-3">
        <h3 class="text-xl font-bold text-center intro-x xl:text-left mb-5">
          Iniciar sesión
        </h3>
        <div v-if="esMobile" class="flex-1 text-right">
          <Button
            variant="primary"
            class="mb-2 mr-1"
            @click="handlerIniciarSesionRUT"
          >
            Acceder
          </Button>
        </div>
        <div class="flex flex-col">
          <div class="mb-4 text-left">
            <FormLabel htmlFor="ingresa-mail-registro"
              >Ingresa tu RUT</FormLabel
            >
            <FormInput
              id="ingresa-rut"
              v-model="inicioSesionRUT"
              type="text"
              placeholder="Ingresa tu RUT"
            />
          </div>
          <div class="mb-4 text-left">
            <FormLabel htmlFor="ingresa-mail-registro"
              >Ingresa tu usuario</FormLabel
            >
            <FormInput
              id="ingresa-usuario"
              v-model="inicioSesionUsuario"
              type="text"
              placeholder="Ingresa tu usuario"
            />
            <template v-if="usuarioInvalido != ''">
              <p class="text-danger mt-3">{{ usuarioInvalido }}</p>
            </template>
          </div>
          <div class="flex">
            <div class="mb-4 text-left">
              <FormLabel htmlFor="ingresa-contrasena-registro"
                >Ingresa tu contraseña para Atiendo</FormLabel
              >
              <FormInput
                id="ingresa-contrasena-acceso-rut"
                v-model="inicioSesionRUTUsuarioClave"
                :type="verPassword ? 'text' : 'password'"
                placeholder="Ingresa tu contraseña"
              />
            </div>
            <Lucide
              @click="handlerPassword"
              icon="Eye"
              class="w-4 h-4 m-3 cursor-pointer mt-10"
            />
          </div>
        </div>
      </div>
      <div v-if="inicioSesionRUTMensajeError != ''" class="mt-3 text-left">
        <div class="text-danger">
          {{ inicioSesionRUTMensajeError }}
        </div>
      </div>
      <div v-if="!cargandoLoginRUT" class="flex my-6">
        <!-------------------- Botón Cancelar ------------------------>
        <div class="flex-1 text-left">
          <Button
            variant="secondary"
            class="mb-2 mr-1"
            @click="() => (mecanismo = 'servicios')"
          >
            Cancelar
          </Button>
        </div>
        <!-------------------- Botón Crear nuevo ------------------------>
        <div v-if="!esMobile" class="flex-1 text-right">
          <Button
            variant="primary"
            class="mb-2 mr-1"
            @click="handlerIniciarSesionRUT"
          >
            Acceder
          </Button>
        </div>
      </div>

      <div v-else class="flex justify-center my-6">
        <div class="flex">
          <LoadingIcon icon="oval" class="w-8 h-8" />
        </div>
      </div>
    </div>
    <div
      v-else-if="mecanismo == 'magicLink'"
      class="mt-8 intro-x flex flex-col text-left"
    >
      <div v-if="useMagicLinkRealizandoLogin">
        <div v-if="useMagicLinkAccesoMensajeError == ''">
          <div
            class="flex flex-col items-center justify-end col-span-6 sm:col-span-3 xl:col-span-2"
          >
            <div class="w-64">
              <div class="flex flex-col items-center">
                <div class="w-8">
                  <LoadingIcon icon="oval" class="w-8 h-8" />
                </div>
              </div>
              <p class="mt-5 text-center">Iniciando sesión</p>
            </div>
          </div>
        </div>
        <div v-if="useMagicLinkAccesoMensajeError != ''" class="mt-3">
          <Alert variant="outline-danger" class="flex items-center mb-2">
            {{ useMagicLinkAccesoMensajeError }}
          </Alert>
          <Button
            variant="secondary"
            class="mb-2 mr-1 mt-5"
            @click="reintentarMagicLicnk"
          >
            Volver a intentarlo
          </Button>
        </div>
      </div>
      <div v-else>
        <div class="mt-3">
          <FormLabel htmlFor="ingresa-mail">Ingresa tu mail</FormLabel>
          <FormInput
            id="ingresa-mail"
            v-model="useMagicLinkEmail"
            type="text"
            placeholder="Ingresa tu mail"
          />
          <FormHelp>
            Te enviaremos un mail con un link o con un código, según elijas,
            para ingresar a Atiendo.
          </FormHelp>
        </div>
        <div v-if="useMagicLinkMensajeError != ''" class="mt-3">
          <Alert variant="outline-danger" class="flex items-center mb-2">
            {{ useMagicLinkMensajeError }}
          </Alert>
        </div>
        <div v-if="useMagicLinkMensajeExito != ''" class="mt-3">
          <Alert variant="outline-success" class="flex items-center mb-2">
            <Lucide icon="Lock" class="w-6 h-6 mr-2" />
            {{ useMagicLinkMensajeExito }}
          </Alert>
        </div>

        <div v-if="!useMagicLinkCargando" class="mb-6 mt-4">
          <!-------------------- Botón Crear nuevo ------------------------>
          <div class="flex">
            <div class="flex-1 text-left">
              <Button
                variant="primary"
                class="mb-2 px-4"
                @click="handlerEnviarMail"
              >
                Enviar mail de acceso
              </Button>
            </div>
            <div class="flex-1 text-right">
              <Button
                variant="primary"
                class="mb-2 px-4"
                @click="handlerEnviarMailCodigo"
              >
                Enviar código de acceso
              </Button>
            </div>
          </div>
          <!-------------------- Botón Cancelar ------------------------>
          <div class="flex-1 text-center mt-4">
            <Button
              variant="secondary"
              class="mb-2 mr-1 px-4"
              @click="() => (mecanismo = 'servicios')"
            >
              Cancelar
            </Button>
          </div>
        </div>

        <div v-else class="flex justify-center my-6">
          <div class="flex">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </div>
    </div>

    <div
      v-else-if="mecanismo == 'codigo'"
      class="mt-8 intro-x flex flex-col text-left"
    >
      <div v-if="useCodigoRealizandoLogin">
        <div v-if="useCodigoAccesoMensajeError == ''">
          <div
            class="flex flex-col items-center justify-end col-span-6 sm:col-span-3 xl:col-span-2"
          >
            <div class="w-64">
              <div class="flex flex-col items-center">
                <div class="w-8">
                  <LoadingIcon icon="oval" class="w-8 h-8" />
                </div>
              </div>
              <p class="mt-5 text-center">Iniciando sesión</p>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-3" v-if="!codigoEnviado">
        <FormLabel htmlFor="ingresa-mail">Ingresa tu mail</FormLabel>
        <FormInput
          id="ingresa-mail"
          v-model="useMagicLinkEmail"
          type="text"
          placeholder="Ingresa tu mail"
        />
        <FormHelp>
          Te enviaremos un mail con un link o con un código, según elijas, para
          ingresar a Atiendo.
        </FormHelp>
        <div v-if="enviarMailCodigoError != ''" class="mt-3">
          <Alert variant="outline-danger" class="flex items-center mb-2">
            {{ enviarMailCodigoError }}
          </Alert>
        </div>
        <!-------------------- Botón Crear nuevo ------------------------>
        <div class="flex">
          <div class="flex-1 text-left">
            <Button
              variant="primary"
              class="mb-2 px-4"
              @click="handlerEnviarMail"
            >
              Enviar mail de acceso
            </Button>
          </div>
          <div class="flex-1 text-right">
            <Button
              variant="primary"
              class="mb-2 px-4"
              @click="handlerEnviarMailCodigo"
            >
              Enviar código de acceso
            </Button>
          </div>
        </div>
        <!-------------------- Botón Cancelar ------------------------>
        <div class="flex-1 text-center mt-4">
          <Button
            variant="secondary"
            class="mb-2 mr-1 px-4"
            @click="() => (mecanismo = 'servicios')"
          >
            Cancelar
          </Button>
        </div>
      </div>
      <div v-else>
        <div v-if="enviarMailCodigoError != ''" class="mt-3">
          <Alert variant="outline-danger" class="flex items-center mb-2">
            {{ enviarMailCodigoError }}
          </Alert>
        </div>
        <div
          v-if="
            enviarMailCodigoExito != '' &&
            codigoEnviado &&
            !useCodigoAccesoMensajeError
          "
          class="mt-3"
        >
          <Alert variant="outline-success" class="flex items-center mb-4">
            <Lucide icon="Lock" class="w-6 h-6 mr-2" />
            {{ enviarMailCodigoExito }}
          </Alert>
        </div>
        <div v-if="useCodigoAccesoMensajeError != ''" class="mt-3">
          <Alert variant="outline-danger" class="flex items-center mb-2">
            {{ useCodigoAccesoMensajeError }}
          </Alert>
        </div>
        <div
          v-if="
            useCodigoAccesoMensajeError !=
              'Error generando token de autenticación. Intenta nuevamente más tarde.' &&
            useCodigoAccesoMensajeError !=
              'Usuario no registrado. Primero debes registrarte en la web.'
          "
          class="flex gap-2 justify-center"
        >
          <input
            v-for="(digit, index) in code"
            :key="index"
            type="text"
            maxlength="1"
            class="dark:bg-darkmode-800 w-12 h-12 text-center border dark:focus:ring-slate-700 border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xl"
            v-model="code[index]"
            @input="handleInput(index, $event)"
            @keydown.backspace="handleBackspace(index, $event)"
            ref="inputs"
          />
        </div>
        <div class="flex mt-4">
          <div class="flex-1 text-left">
            <Button
              variant="secondary"
              class="mb-2 px-4"
              @click="() => (mecanismo = 'servicios')"
            >
              Cancelar
            </Button>
          </div>
          <div class="flex-1 text-right">
            <Button
              variant="primary"
              class="mb-2 px-4"
              @click="handlerEnviarMailCodigo"
            >
              Reenviar código
            </Button>
          </div>
        </div>

        <div
          v-if="useCodigoAccesoCargando || useCodigoCargando"
          class="flex justify-center my-6"
        >
          <div class="flex">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div
      class="flex flex-col items-center justify-end col-span-6 sm:col-span-3 xl:col-span-2"
    >
      <div class="w-64">
        <div class="flex flex-col items-center">
          <div class="w-8">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
        <p class="mt-5 text-center">{{ cargandoMensaje }}</p>
        <div class="flex justify-center mt-5">
          <div class="flex-1">
            <Button
              variant="secondary"
              size="sm"
              class="mb-2 mr-1"
              @click="cancelarLogin"
            >
              Cerrar sesión
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style type="text/css" scoped>
#customBtn {
  display: inline-block;
  background: white;
  color: #444;
  width: 190px;
  border-radius: 5px;
  border: thin solid #888;
  box-shadow: 1px 1px 1px grey;
  white-space: nowrap;
}
#customBtn:hover {
  cursor: pointer;
}
span.label {
  font-family: serif;
  font-weight: normal;
}

span.iconProvider {
  display: inline-block;
  vertical-align: middle;
  width: 42px;
  height: 42px;
  padding: 8px;
}
span.buttonText {
  display: inline-block;
  vertical-align: middle;
  padding-left: 18px;
  padding-right: 42px;
  font-size: 14px;
  font-weight: bold;
  /* Use the Roboto font that is loaded in the <head> */
  font-family: "Roboto", sans-serif;
}
</style>
